import { Box, Step, StepButton, StepLabel, Stepper, makeStyles, useTheme } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import DynamicForm from "./DynamicForm";

function DynamicStepper(props) {
    const { _config, sectionKey, AddNewField, navigateToNextTab, subTabStatus, userAccess, userSiteTrialRole, onFormChanged } = props;
    const theme = useTheme();
    const [activeStep, setActiveStep] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Initialize activeStep from URL query params
    useEffect(() => {
        if (!_config || !sectionKey) return;

        const querySectionKey = searchParams.get('sectionKey');
        const querySubSectionKey = searchParams.get('subSectionKey');
        const currentSubTab = _config[activeStep];

        // Only process if the sectionKey matches
        if (querySectionKey === sectionKey && querySubSectionKey && querySubSectionKey !== currentSubTab?.key) {
            const subSectionIndex = _config.findIndex(
                (subTab) => subTab.key === querySubSectionKey
            );
            if (subSectionIndex !== -1) {
                setActiveStep(subSectionIndex);
                // Call onFormChanged callback if provided
                if (onFormChanged) {
                    onFormChanged(sectionKey, querySubSectionKey);
                }
            }
        } else if (querySectionKey === sectionKey && !querySubSectionKey && _config.length > 0) {
            // Set default to first subTab if section matches but no subSectionKey
            const defaultSubTab = _config[0];
            const params = new URLSearchParams(searchParams);
            params.set('sectionKey', sectionKey);
            params.set('subSectionKey', defaultSubTab.key);
            setSearchParams(params, { replace: true });
            if (onFormChanged) {
                onFormChanged(sectionKey, defaultSubTab.key);
            }
        }
    }, [_config, sectionKey, searchParams, onFormChanged]);
    
    const handleStep = (step) => () => {
        const selectedSubTab = _config[step];
        setActiveStep(step);
        
        // Update URL query params
        const params = new URLSearchParams(searchParams);
        params.set('sectionKey', sectionKey);
        params.set('subSectionKey', selectedSubTab.key);
        setSearchParams(params, { replace: true });
        
        // Call onFormChanged callback if provided
        if (onFormChanged) {
            onFormChanged(sectionKey, selectedSubTab.key);
        }
        
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
    };
    const navigateToNextSubTab = () => {
        const nextTabInd = activeStep + 1;
        if (_config && _config.length > nextTabInd) {
            const nextSubTab = _config[nextTabInd];
            setActiveStep(nextTabInd);
            
            // Update URL query params
            const params = new URLSearchParams(searchParams);
            params.set('sectionKey', sectionKey);
            params.set('subSectionKey', nextSubTab.key);
            setSearchParams(params, { replace: true });
            
            // Call onFormChanged callback if provided
            if (onFormChanged) {
                onFormChanged(sectionKey, nextSubTab.key);
            }
            
            document.documentElement.scrollTop = 0;
            document.scrollingElement.scrollTop = 0;
        } else if (_config && _config.length == nextTabInd) {
            navigateToNextTab()
        }
    }
    return (
        <React.Fragment>
            <Box sx={{ display: "flex", width: "100%", justifyContent: "left" }}>
                <Box
                    sx={{
                        width: "30%",
                        padding: 3,
                        pl: 2
                    }}
                >
                    <Stepper nonLinear orientation="vertical" activeStep={activeStep}
                        sx={{
                            "& .MuiStepConnector-root": {
                                left: "calc(-50% + 12px)",
                                right: "calc(50% + 12px)",
                            },
                            "& .MuiStepConnector-line": {
                                borderWidth: 6,
                                minHeight: 40
                            },
                            "& .MuiStepLabel-vertical": {
                                py: 0
                            },
                            "& .MuiStepLabel-iconContainer": {
                                ml: 0.30,
                                height: "0.7rem",
                                width: "0.2rem",
                                border: `7px solid ${theme.palette.grey[500]}`,
                                borderRadius: "50%",
                            },
                            "& .MuiStepLabel-labelContainer": {
                                paddingLeft: 1,
                                "& .MuiStepLabel-label": {
                                    fontSize: 16,
                                    fontWeight: 600
                                }
                            },
                            "& .MuiStep-root.Mui-active": {
                                "& .MuiStepLabel-iconContainer": {
                                    backgroundColor: theme.palette.common.white,
                                    border: `7px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 0 0 0px ${theme.palette.primary.light}, inset 0 0 0 5px ${theme.palette.primary.light}`,
                                },
                                "& .MuiStepLabel-label": {
                                    color: theme.palette.primary.main,
                                    fontWeight: 700,
                                },
                            },
                            "& .Mui-completed": {
                                "& .MuiStepConnector-line": {
                                    borderColor: theme.palette.primary.light,
                                    borderWidth: 6,
                                },
                                "& .MuiStepLabel-iconContainer": {
                                    backgroundColor: theme.palette.common.white,
                                    border: "7px solid green",
                                },
                            },
                        }}>
                        {_config &&
                            _config.map((_step, ind) => {
                                let status = subTabStatus?.find((subSection) => subSection.subSectionKey === _step.key)
                                let completed = false;
                                const isVerifyAccess = userAccess?.verify?.find((_role) => _role === userSiteTrialRole);
                                const isEditAccess = userAccess?.edit?.find((_role) => _role === userSiteTrialRole);

                                if (!isVerifyAccess && isEditAccess && status?.status == 'Verification_Pending') {
                                  completed = true;
                                } else if (status?.status === 'Completed' || status?.status == 'External_Verification_Pending' ) {
                                  completed = true;
                                }
                                
                               const isActive = ind === activeStep;
                               return <Step key={_step.key} completed={completed}>
                                    <StepButton
                                        color="inherit"
                                        // disabled={_step.status == "Pending"}
                                        onClick={handleStep(ind)}
                                        icon={<Box></Box>}
                                    >
                                        <StepLabel 
                                            sx={{ 
                                                paddingx: 3,
                                                "& .MuiStepLabel-iconContainer": isActive ? {
                                                    backgroundColor: theme.palette.common.white,
                                                    border: `7px solid ${theme.palette.primary.light}`,
                                                    boxShadow: `0 0 0 0px ${theme.palette.primary.light}, inset 0 0 0 2px ${theme.palette.primary.light}`,
                                                } : {},
                                                "& .MuiStepLabel-label": isActive ? {
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 700,
                                                } : {},
                                            }}
                                        >
                                            {_step.label}
                                        </StepLabel>
                                    </StepButton>
                                    {/* <StepLabel>{_step.label}</StepLabel> */}
                                </Step>
                            })}
                    </Stepper>
                </Box>
                <Box sx={{ width: "70%" }}>
                    {_config &&
                        _config.map((tab, index) => (
                            activeStep === index && (tab.categories || tab.fields) && (
                                <Box key={`step${index}`}><DynamicForm
                                    {...props}
                                    _config={tab}
                                    sectionKey={sectionKey}
                                    subSectionKey={tab.key}
                                    navigateToNextTab={navigateToNextSubTab}
                                /></Box>
                            )
                        ))}
                </Box>
            </Box>
        </React.Fragment>
    );
}

export default React.memo(DynamicStepper);
;
